import React, { useState, useRef, useEffect } from "react";
import { FileText, Globe, Code2, BarChart2, Share2, ImageIcon, Video, AlignLeft, ChevronLeft, ChevronRight } from "lucide-react";
import MarkdownRenderer from "../Markdown/MarkdownRenderer";
import ReactViz from "../Markdown/ReactViz";
import PhysicsViz from "../Markdown/PhysicsViz";
import MermaidDiagram from "../Markdown/MermaidDiagram";
import { parseNebulaResponse } from "../../utils/nebulaParser";
import { prepareHtml } from "../../utils/nebulaHtml";
import { transformContent } from "../../utils/helpers";
import { getDisplayAnswerId } from "../../store/promptStore";
import useStreamingStateStore from "../../store/useStreamingStateStore";
import { FILTER, getActivityTitle, projectDelegationLifecycle } from "../shared/Timeline/activityItemUtils";
import useResolvedMediaUrl from "../../hooks/useResolvedMediaUrl";

// Fixed card width. Card height grows to fit the full artifact at a fixed scale.
const CARD_W = 440;
const IFRAME_W = 1280;                    // logical desktop width
const IFRAME_SCALE = CARD_W / IFRAME_W;   // ~0.344
const HTML_CARD_MIN_H = 260;              // minimum thumb height
const HTML_CARD_MAX_H = 8000;             // safety cap for runaway pages

// Type metadata: icon component + label
const TYPE_META = {
  html:    { Icon: Globe,    label: "Web"       },
  react:   { Icon: Code2,    label: "Component" },
  plotly:  { Icon: BarChart2,label: "Chart"     },
  mermaid: { Icon: Share2,   label: "Diagram"   },
  image:   { Icon: ImageIcon,label: "Image"     },
  video:   { Icon: Video,    label: "Video"     },
  text:    { Icon: FileText, label: "Text"      },
};

// ── Placeholder (before IntersectionObserver fires, or for streaming card) ──
function CardPlaceholder({ type, isStreaming, activityItems = [], height = 200 }) {
  const { Icon, label } = TYPE_META[type] ?? TYPE_META.text;
  const filteredActivities = React.useMemo(
    () => projectDelegationLifecycle((activityItems || []).filter(FILTER)),
    [activityItems]
  );
  const latestActivity = filteredActivities[filteredActivities.length - 1] ?? activityItems?.[activityItems.length - 1];
  const streamingLabel = (latestActivity && (getActivityTitle(latestActivity) || latestActivity.label)) || "Beaming";
  const streamingDetail = latestActivity?.description?.trim() || null;

  return (
    <div className="relative w-full flex flex-col items-center justify-center gap-2 px-6 bg-fill-hex-f5f5f5 dark:bg-fill-canvas-page-surface" style={{ height }}>
      {isStreaming && (
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(75,159,225,0.08) 40%, rgba(75,159,225,0.18) 50%, rgba(75,159,225,0.08) 60%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "nebula-shimmer 1.6s ease-in-out infinite",
          }}
        />
      )}
      <Icon size={28} className="relative text-ink-gray-400 dark:text-ink-gray-500" strokeWidth={1.5} />
      {isStreaming ? (
        <div className="relative flex flex-col items-center gap-1 max-w-full text-center">
          <span className="text-[11px] text-ink-canvas-accent font-medium animate-pulse truncate max-w-full">
            {streamingLabel}
            <span className="beaming-dots">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </span>
          </span>
          {streamingDetail && (
            <span className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400 italic line-clamp-2 max-w-[340px]">
              {streamingDetail}
            </span>
          )}
        </div>
      ) : (
        <span className="relative text-[11px] text-ink-gray-400 dark:text-ink-gray-500 font-medium">{label}</span>
      )}
    </div>
  );
}

// ── HTML thumbnail ─ card grows to fit full page at fixed scale ──────────────────
function HtmlThumbnail({ content }) {
  const iframeRef = useRef(null);
  const [pageH, setPageH] = useState(800);
  const srcDoc = React.useMemo(() => prepareHtml(content), [content]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let cancelled = false;
    let ro = null;

    function measure() {
      try {
        const doc = iframe.contentDocument;
        if (!doc || !doc.body) return;
        const h = Math.max(
          doc.body.scrollHeight,
          doc.documentElement.scrollHeight,
          doc.body.offsetHeight,
          doc.documentElement.offsetHeight,
        );
        if (h > 0 && !cancelled) setPageH(h);
      } catch (_) { /* cross-origin */ }
    }

    function attach() {
      measure();
      try {
        const win = iframe.contentWindow;
        if (win && win.ResizeObserver && iframe.contentDocument?.body) {
          ro = new win.ResizeObserver(measure);
          ro.observe(iframe.contentDocument.body);
        }
      } catch (_) { /* ignore */ }
      setTimeout(measure, 200);
      setTimeout(measure, 800);
    }

    iframe.addEventListener("load", attach);
    if (iframe.contentDocument?.readyState === "complete") attach();
    return () => {
      cancelled = true;
      iframe.removeEventListener("load", attach);
      try { ro?.disconnect(); } catch (_) { /* ignore */ }
    };
  }, [srcDoc]);

  // Card height = scaled page height. Each card sized to its own content.
  const cardH = Math.min(HTML_CARD_MAX_H, Math.max(HTML_CARD_MIN_H, Math.round(pageH * IFRAME_SCALE)));

  return (
    <div style={{ width: CARD_W, height: cardH, overflow: "hidden", position: "relative", background: "rgb(var(--photon-fill-white))", transition: "height 200ms ease" }}>
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        title="preview"
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: IFRAME_W,
          height: pageH,
          transform: `scale(${IFRAME_SCALE})`,
          transformOrigin: "top left",
          pointerEvents: "none",
          border: "none",
          display: "block",
        }}
      />
    </div>
  );
}

// ── Scaled wrapper for React / Plotly / Mermaid ──────────────────────────────
const CODE_RENDER_W = 1200;
const CODE_RENDER_H = 1800;         // tall render area so multi-section components show fully
const CODE_SCALE = CARD_W / CODE_RENDER_W;
const CODE_CARD_H = Math.round(CODE_RENDER_H * CODE_SCALE);

function ScaledCodeThumbnail({ type, content }) {
  return (
    <div
      style={{ width: CARD_W, height: CODE_CARD_H, overflow: "hidden", position: "relative" }}
      className="bg-fill-white dark:bg-fill-hex-0d1117"
    >
      <div
        style={{
          width: CODE_RENDER_W,
          height: CODE_RENDER_H,
          transform: `scale(${CODE_SCALE})`,
          transformOrigin: "top left",
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {type === "react"   && <ReactViz code={content} hideControls />}
        {type === "plotly"  && <PhysicsViz code={content} hideControls />}
        {type === "mermaid" && <MermaidDiagram chart={content} hideControls />}
      </div>
    </div>
  );
}


// ── Image thumbnail ──────────────────────────────────────────────────────────
function ImageThumbnail({ content, isStoragePath }) {
  const url = useResolvedMediaUrl(content, isStoragePath);
  return (
    <div
      style={{ width: CARD_W, minHeight: 180 }}
      className="flex items-center justify-center bg-fill-hex-f5f5f5 dark:bg-fill-canvas-page-surface"
    >
      {url ? (
        <img
          src={url}
          alt="artifact"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      ) : (
        <ImageIcon size={28} className="text-ink-gray-400 dark:text-ink-gray-500" strokeWidth={1.5} />
      )}
    </div>
  );
}

// ── Video thumbnail ──────────────────────────────────────────────────────────
function VideoThumbnail({ content, isStoragePath }) {
  const url = useResolvedMediaUrl(content, isStoragePath);
  return (
    <div
      style={{ width: CARD_W, minHeight: 180 }}
      className="flex items-center justify-center bg-fill-hex-f5f5f5 dark:bg-fill-canvas-page-surface"
    >
      {url ? (
        <video
          src={url}
          style={{ width: "100%", height: "auto", display: "block" }}
          muted
        />
      ) : (
        <Video size={28} className="text-ink-gray-400 dark:text-ink-gray-500" strokeWidth={1.5} />
      )}
    </div>
  );
}

// ── Text thumbnail ───────────────────────────────────────────────────────────
function TextThumbnail({ content }) {
  const { cleanedText } = transformContent(content ?? "");
  return (
    <div
      style={{ width: CARD_W }}
      className="flex flex-col gap-2 p-3 bg-fill-hex-f5f5f5 dark:bg-fill-canvas-page-surface"
    >
      <div className="flex items-center gap-1.5">
        <FileText size={13} className="text-ink-gray-400 dark:text-ink-gray-500" strokeWidth={1.5} />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-gray-400 dark:text-ink-gray-500">Text</span>
      </div>
      <div className="text-[11px] text-ink-gray-600 dark:text-ink-gray-400 leading-relaxed pointer-events-none overflow-hidden">
        <MarkdownRenderer content={cleanedText} />
      </div>
    </div>
  );
}

// ── Single overview card ─────────────────────────────────────────────────────
function OverviewCard({ message, index, isActive, isStreaming, showReportText, selectedAnswerByPrompt = {}, onClick }) {
  const [cardItemIndex, setCardItemIndex] = useState(0);
  const msgId = message?.id;
  const streamEntry = useStreamingStateStore((state) => (msgId ? state.activeStreams[msgId] : undefined));
  const isCardStreaming = isStreaming || !!streamEntry?.streaming;
  const answerId = selectedAnswerByPrompt[msgId] || getDisplayAnswerId(message?.answers);
  const activeAnswer = message?.answers?.find((a) => a.id === answerId) ?? message?.answers?.slice(-1)[0];
  const activityItems = streamEntry?.activityItems?.length
    ? streamEntry.activityItems
    : (activeAnswer?.activity || []);
  const parsedArtifact = parseNebulaResponse(activeAnswer?.content ?? "");
  const items = Array.isArray(parsedArtifact?.items) && parsedArtifact.items.length > 0
    ? parsedArtifact.items
    : (parsedArtifact ? [parsedArtifact] : []);
  const safeItemIndex = items.length > 0 ? Math.min(cardItemIndex, items.length - 1) : 0;
  const artifact = items[safeItemIndex] ?? parsedArtifact;
  const type = artifact?.type ?? "text";
  const { Icon, label } = TYPE_META[type] ?? TYPE_META.text;
  const preamble = transformContent(artifact?.preamble ?? "").cleanedText;
  const postamble = transformContent(artifact?.postamble ?? "").cleanedText;

  // Title = the user's prompt (truncated). Fallback to artifact label.
  const title = (message?.content ?? "").trim().split("\n")[0].slice(0, 60) || `${label} ${index + 1}`;

  const thumbH = type === "html" ? HTML_CARD_MIN_H
    : (type === "react" || type === "plotly" || type === "mermaid") ? CODE_CARD_H
    : 180;

  const renderThumbnail = () => {
    if (isCardStreaming) return <CardPlaceholder type={type} isStreaming activityItems={activityItems} height={thumbH} />;
    if (!artifact) return <CardPlaceholder type={type} isStreaming={false} height={thumbH} />;

    switch (type) {
      case "html":    return <HtmlThumbnail content={artifact.content} />;
      case "react":
      case "plotly":
      case "mermaid": return <ScaledCodeThumbnail type={type} content={artifact.content} />;
      case "image":   return <ImageThumbnail content={artifact.content} isStoragePath={artifact.isStoragePath} />;
      case "video":   return <VideoThumbnail content={artifact.content} isStoragePath={artifact.isStoragePath} />;
      case "text":    return <TextThumbnail content={artifact.content} />;
      default:        return <CardPlaceholder type={type} isStreaming={false} />;
    }
  };

  const hasArtifact = type !== "text";

  return (
    <div className="flex flex-col gap-2" style={{ width: CARD_W }}>
      {/* Title above card */}
      <div className="flex items-center justify-between gap-1.5 px-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={11} className="text-ink-gray-400 dark:text-ink-gray-500 shrink-0" strokeWidth={1.5} />
          <span className="text-[11px] font-medium text-ink-gray-600 dark:text-ink-toolbar-border truncate" title={title}>{title}</span>
        </div>
        {items.length > 1 && !isCardStreaming && (
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setCardItemIndex((safeItemIndex - 1 + items.length) % items.length)}
              className="p-0.5 rounded hover:bg-fill-gray-100 dark:hover:bg-fill-canvas-page-surface text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white transition-colors"
              title="Previous artifact"
              aria-label="Previous artifact"
            >
              <ChevronLeft size={11} />
            </button>
            <span className="text-[10px] tabular-nums text-ink-gray-400 dark:text-ink-gray-500 font-medium select-none">
              {safeItemIndex + 1}/{items.length}
            </span>
            <button
              type="button"
              onClick={() => setCardItemIndex((safeItemIndex + 1) % items.length)}
              className="p-0.5 rounded hover:bg-fill-gray-100 dark:hover:bg-fill-canvas-page-surface text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white transition-colors"
              title="Next artifact"
              aria-label="Next artifact"
            >
              <ChevronRight size={11} />
            </button>
          </div>
        )}
      </div>

      {/* Card frame — wraps preamble + artifact + postamble together */}
      <div
        className={`relative rounded-lg cursor-pointer transition-all duration-150 bg-fill-white dark:bg-fill-canvas-page-surface shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden
          ${isActive
            ? "ring-2 ring-focus-canvas-accent shadow-[0_4px_16px_rgba(75,159,225,0.25)]"
            : "ring-1 ring-focus-gray-200 dark:ring-focus-panel-surface hover:ring-focus-canvas-accent/50 hover:shadow-[0_4px_12px_rgba(0,0,0,0.10)]"
          }`}
        onClick={onClick}
      >
        {/* Preamble text — shown above the artifact when report text is enabled */}
        {showReportText && preamble && hasArtifact && (
          <div className="px-3.5 pt-3 pb-2 border-b border-edge-hex-f0f0f0 dark:border-edge-hex-2a2a2a text-[11px] leading-relaxed text-ink-gray-700 dark:text-ink-toolbar-border pointer-events-none max-h-40 overflow-hidden">
            <MarkdownRenderer content={preamble} />
          </div>
        )}

        {/* Artifact thumbnail */}
        <div className="relative">
          {renderThumbnail()}
          {/* Click overlay — on top of everything, captures clicks */}
          <div className="absolute inset-0" style={{ cursor: "pointer" }} />
        </div>

        {/* Postamble text — shown below the artifact when report text is enabled */}
        {showReportText && postamble && hasArtifact && (
          <div className="px-3.5 pt-2 pb-3 border-t border-edge-hex-f0f0f0 dark:border-edge-hex-2a2a2a text-[11px] leading-relaxed text-ink-gray-700 dark:text-ink-toolbar-border pointer-events-none max-h-40 overflow-hidden">
            <MarkdownRenderer content={postamble} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main NebulaOverview ───────────────────────────────────────────────────────
const NebulaOverview = ({ messages = [], activeIndex, selectedAnswerByPrompt = {}, isStreaming = false, onSelectIndex }) => {
  const [showReportText, setShowReportText] = useState(false);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-fill-white dark:bg-fill-hex-0f1419">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-end px-6 py-2 border-b border-edge-hex-f0f0f0 dark:border-edge-canvas-page-surface">
        <button
          onClick={() => setShowReportText(v => !v)}
          title={showReportText ? "Hide context" : "Show context"}
          className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-md transition-colors ${
            showReportText
              ? "bg-fill-canvas-accent/15 text-ink-canvas-accent border border-edge-canvas-accent/30"
              : "text-ink-gray-500 dark:text-ink-gray-400 hover:bg-fill-gray-100 dark:hover:bg-fill-canvas-page-surface border border-transparent"
          }`}
        >
          <AlignLeft className="w-3.5 h-3.5" />
          {showReportText ? "Hide context" : "Show context"}
        </button>
      </div>

      {/* Cards grid */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="p-8 flex flex-wrap items-start gap-6 justify-center">
          {messages.map((msg, i) => {
            const isStreamingCard = isStreaming && i === messages.length - 1;
            return (
              <OverviewCard
                key={msg.id ?? i}
                message={msg}
                index={i}
                isActive={i === activeIndex}
                isStreaming={isStreamingCard}
                showReportText={showReportText}
                selectedAnswerByPrompt={selectedAnswerByPrompt}
                onClick={() => onSelectIndex(i)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NebulaOverview;
